
import Vue from 'vue'
import {Component, Prop, Watch} from 'vue-property-decorator'
import UiIcon from '@/components/common/UiIcon.vue'
import RefMultiSelect from '@/components/common/RefMultiSelect.vue'
import SelectRef from '@/components/common/SelectRef.vue'
import SelectConseiller from '@/components/common/SelectConseiller.vue'
import SelectGroupe from '@/components/common/SelectGroupe.vue'
import CheckboxYesNo from '@/components/common/CheckboxYesNo.vue'
import {isEqualObject, normalizeDataForm, currentConseillerId} from '@/helpers'
import {namespace} from 'vuex-class'
import convertDateDirective from '@/directives/convertDateDirective'

const uiModule = namespace('ui')
const authModule = namespace('auth')

@Component({
  components: {
    SelectRef,
    SelectConseiller,
    SelectGroupe,
    CheckboxYesNo,
    UiIcon,
    RefMultiSelect
  },
  directives: {
    convertDateDirective
  }
})
export default class ProspectFilter extends Vue {
  @Prop(Number) count!: number
  @Prop(Object) data!: any
  @uiModule.Getter('isLoadingGetter') isLoading: any
  @authModule.Getter('meGetter') me: any
  advancedSearch = false
  contactSearch = false
  form = {
    recherche: '',
    pays: '',
    departement: '',
    propositionEnCours: null,
    essaiEnCours: null,
    demo: null,
    refStatuts: [],
    refSituations: [],
    inGroupes: [],
    outGroupes: [],
    rechercheContact: '',
    telephoneContact: '',
    noTelephoneContact: 0,
    emailContact: '',
    noEmailContact: 0,
    origine: '',
    typeEntite: '',
    refTailles: [],
    typeDemande: '',
    urgenceDemande: '',
    conseiller: currentConseillerId(),
    motsCles: '',
    syndicCooperatif: 0,
    syndicSecondaire: 0,
    employes: 0,
    dateEchangeMin: '',
    dateEchangeMax: '',
    dateOuvertureMin: '',
    dateOuvertureMax: '',
    dateProchaineActionMin: '',
    dateProchaineActionMax: '',
    lotMin: '',
    lotMax: '',
    nouvelEchangeATraiter: 0
  }

  advancedSearchFields = [
    'origine',
    'typeEntite',
    'refTailles',
    'refSituations',
    'typeDemande',
    'urgenceDemande',
    'syndicCooperatif',
    'syndicSecondaire',
    'employes',
    'dateOuvertureMin',
    'dateOuvertureMax',
    'dateEchangeMin',
    'dateEchangeMax',
    'dateProchaineActionMin',
    'dateProchaineActionMax',
    'lotMin',
    'lotMax',
    'motsCles',
    'inGroupes',
    'outGroupes',
    'nouvelEchangeATraiter'
  ]

  contactFields = ['rechercheContact', 'telephoneContact', 'noTelephoneContact', 'emailContact', 'noEmailContact']
  booleanFields = [
    'noTelephoneContact',
    'noEmailContact',
    'syndicCooperatif',
    'syndicSecondaire',
    'employes',
    'nouvelEchangeATraiter'
  ]

  filterLabels = {
    recherche: 'Prospect',
    pays: 'Pays',
    departement: 'Département',
    propositionEnCours: 'Proposition',
    essaiEnCours: 'Essai',
    demo: 'Démo',
    refStatuts: 'Statuts',
    refSituations: 'Situations',
    inGroupes: 'Groupes inclus',
    outGroupes: 'Groupes exclus',
    rechercheContact: 'Contact',
    telephoneContact: 'Téléphone',
    noTelephoneContact: 'Sans téléphone',
    emailContact: 'Email',
    noEmailContact: 'Sans email',
    origine: 'Origine',
    typeEntite: 'Type d’entité',
    refTailles: 'Tailles',
    typeDemande: 'Type de demande',
    urgenceDemande: 'Urgence',
    conseiller: 'Conseiller',
    motsCles: 'Mots clés',
    syndicCooperatif: 'Syndicat coopératif',
    syndicSecondaire: 'Syndicat secondaire',
    employes: 'Employés',
    dateEchangeMin: 'Échange depuis',
    dateEchangeMax: 'Échange jusqu’au',
    dateOuvertureMin: 'Ouverture depuis',
    dateOuvertureMax: 'Ouverture jusqu’au',
    dateProchaineActionMin: 'Action depuis',
    dateProchaineActionMax: 'Action jusqu’au',
    lotMin: 'Lots minimum',
    lotMax: 'Lots maximum',
    nouvelEchangeATraiter: 'Échange à traiter'
  }

  filterValue(key) {
    const value = this.form[key]
    if (this.booleanFields.includes(key)) return ''
    const refNames = {refStatuts: 'statut', refTailles: 'taille', refSituations: 'situation'}
    const refs = this.$store.getters['ref/refGetter'] || {}
    const options =
      key === 'conseiller'
        ? this.$store.getters['conseiller/conseillersGetter']
        : ['inGroupes', 'outGroupes'].includes(key)
        ? this.$store.getters['groupe/groupesGetter']
        : refs[refNames[key] || key]
    const values = Array.isArray(value) ? value : [value]
    if (key === 'conseiller' && String(value) === '0') return 'Non affecté'
    const labels = {
      propositionEnCours: {'0': 'Aucune en cours', '1': 'En cours'},
      essaiEnCours: {'0': 'Terminé', '1': 'En cours', '2': 'Non activé'},
      demo: {'0': 'Non réalisée', '1': 'Réalisée'}
    }
    if (labels[key]) return labels[key][value]
    return values
      .map((selected) => {
        const option = (options || []).find((item) => String(item.CODE || item.ID) === String(selected))
        return option ? option.LIBELLE || option.NOM_UTILISATEUR || option.NOM : selected
      })
      .join(', ')
  }

  hasValue(key) {
    const value = this.form[key]
    if (this.booleanFields.includes(key)) return value === true || value === 1 || value === '1'
    return Array.isArray(value)
      ? value.length > 0
      : value !== '' && value !== null && value !== undefined && value !== 'null'
  }

  get activeFilters() {
    return Object.keys(this.form).filter((key) => this.hasValue(key))
  }

  get advancedCount() {
    return this.advancedSearchFields.filter((key) => this.hasValue(key)).length
  }

  get contactCount() {
    return this.contactFields.filter((key) => this.hasValue(key)).length
  }

  removeFilter(key) {
    const value = this.form[key]
    this.form[key] = Array.isArray(value) ? [] : this.booleanFields.includes(key) ? 0 : ''
    if (['propositionEnCours', 'essaiEnCours', 'demo'].includes(key)) this.form[key] = null
  }

  formDefault = null

  get isFill() {
    return !isEqualObject(this.form, this.formDefault)
  }

  @Watch('me', {deep: false})
  watchMe(val: any) {
    const {conseiller} = this.data || {}
    if (conseiller !== '0' && conseiller !== '') this.form.conseiller = currentConseillerId()
    else this.form.conseiller = conseiller
    if (this.formDefault) this.formDefault.conseiller = this.form.conseiller
  }

  @Watch('form', {deep: true})
  watchData(val: any) {
    if (val.pays !== 'FRA') {
      this.form.departement = ''
    }
  }

  mounted() {
    this.formDefault = Object.assign({}, this.form)
    if (this.data) {
      Object.keys(this.form).map((key, index) => {
        if (this.data[key]) this.form[key] = normalizeDataForm(this.data[key], this.form[key])
      })
      this.checkAdvancedSearch()
      this.contactSearch = this.contactFields.some((key) => this.hasValue(key))
    }
  }

  checkAdvancedSearch() {
    Object.keys(this.data).map((key, index) => {
      const inArray = this.advancedSearchFields.find((item) => item === key)
      if (inArray) this.advancedSearch = true
    })
  }

  onSubmitHandler() {
    this.$emit('filter', {...this.form})
  }

  onResetHandler() {
    this.resetSearchHandler()
    this.form = Object.assign({}, this.formDefault)
    this.$emit('resetFilter')
  }

  resetSearchHandler() {
    this.form = {
      ...this.form,
      recherche: '',
      pays: '',
      departement: '',
      refStatuts: [],
      propositionEnCours: null,
      essaiEnCours: null,
      demo: null,
      conseiller: ''
    }
    this.resetAdvancedSearchHandler()
  }

  resetSearchContactHandler() {
    this.form = {
      ...this.form,
      rechercheContact: '',
      telephoneContact: '',
      noTelephoneContact: 0,
      emailContact: '',
      noEmailContact: 0
    }
  }

  resetAdvancedSearchHandler() {
    this.form = {
      ...this.form,
      typeDemande: '',
      urgenceDemande: '',
      origine: '',
      motsCles: '',
      syndicCooperatif: 0,
      syndicSecondaire: 0,
      employes: 0,
      nouvelEchangeATraiter: 0,
      dateOuvertureMin: '',
      dateOuvertureMax: '',
      dateEchangeMin: '',
      dateEchangeMax: '',
      dateProchaineActionMin: '',
      dateProchaineActionMax: '',
      lotMin: '',
      lotMax: '',
      inGroupes: [],
      outGroupes: [],
      typeEntite: '',
      refTailles: [],
      refSituations: []
    }
  }
}
